import { createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService } from "../../modules/shared/services/http.service";
import {
  Language,
  CreateLanguageData,
  UpdateLanguageData,
  LanguageFilters,
  LanguageResponse
} from "../../modules/shared/model/LanguageInterfaces";

// Get all languages with pagination, sorting, and filtering
export const getAllLanguages = createAsyncThunk(
  "language/getAllLanguages",
  async (filters: LanguageFilters, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        limit = 10,
        search = "",
        sortBy = "createdAt",
        sortOrder = "desc",
        status = "",
        direction = "",
        isDefault,
        isShowLoader = true
      } = filters;

      let endpoint = `api/v1/languages?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      
      if (search) {
        endpoint += `&name=${encodeURIComponent(search)}`;
      }
      if (status) {
        endpoint += `&status=${status}`;
      }
      if (direction) {
        endpoint += `&direction=${direction}`;
      }
      if (isDefault !== undefined) {
        endpoint += `&isDefault=${isDefault}`;
      }

      const response = await HttpService.get(endpoint, isShowLoader, { "Content-Type": "application/json" });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch languages");
    }
  }
);

// Get language by ID
export const getLanguageById = createAsyncThunk(
  "language/getLanguageById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await HttpService.get(`api/v1/languages/${id}`, true, { "Content-Type": "application/json" });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch language");
    }
  }
);

// Create new language
export const createLanguage = createAsyncThunk(
  "language/createLanguage",
  async (data: CreateLanguageData, { rejectWithValue }) => {
    try {
      const response = await HttpService.post("api/v1/languages", data, { "Content-Type": "application/json" }, true);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to create language");
    }
  }
);

// Update language
export const updateLanguage = createAsyncThunk(
  "language/updateLanguage",
  async ({ id, data }: { id: string; data: UpdateLanguageData }, { rejectWithValue }) => {
    try {
      const response = await HttpService.patch(`api/v1/languages/${id}`, data, { "Content-Type": "application/json" }, true);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update language");
    }
  }
);

// Delete language
export const deleteLanguage = createAsyncThunk(
  "language/deleteLanguage",
  async (id: string, { rejectWithValue }) => {
    try {
      await HttpService.deleteRecord(`api/v1/languages/${id}`, { "Content-Type": "application/json" }, true);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete language");
    }
  }
);

// Get language directions
export const getLanguageDirections = createAsyncThunk(
  "language/getLanguageDirections",
  async (_, { rejectWithValue }) => {
    try {
      const response = await HttpService.get("api/v1/language-directions", false, { "Content-Type": "application/json" });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch language directions");
    }
  }
);

// Get language categories
export const getLanguageCategories = createAsyncThunk(
  "language/getLanguageCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await HttpService.get("api/v1/language-categories", false, { "Content-Type": "application/json" });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch language categories");
    }
  }
);

// Get translation settings
export const getTranslationSettings = createAsyncThunk(
  "language/getTranslationSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await HttpService.get("api/v1/translation-settings", false, { "Content-Type": "application/json" });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch translation settings");
    }
  }
);
